import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock, Users, User, UserPlus, Zap, Play, FileVideo } from "lucide-react";
import { formatTime, formatDate, getTimeUntilMeeting, isStartingSoon, MeetingTypeIcon } from "./calendarFormat";

const AttendeeChips = ({ attendees }) => (
  <div className="mb-3">
    <div className="text-xs text-gray-600 mb-1.5 flex items-center gap-1"><Users className="h-3 w-3" /> Meeting With</div>
    <div className="flex flex-wrap gap-2">
      {attendees.slice(0, 5).map((attendee, idx) => (
        <motion.div
          key={attendee.email || attendee.name || idx}
          className="flex items-center gap-1.5 bg-ism-teal/10 rounded-full px-2.5 py-1 border border-ism-teal/20"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.05 }}
        >
          <User className="h-3 w-3 text-ism-teal" />
          <span className="text-xs text-gray-200">{attendee.name || attendee.email?.split("@")[0]}</span>
          {attendee.company && <span className="text-xs text-ism-teal">@ {attendee.company}</span>}
        </motion.div>
      ))}
      {attendees.length > 5 && <span className="text-xs text-gray-500">+{attendees.length - 5} more</span>}
    </div>
  </div>
);

const AddAttendee = ({ onAdd }) => {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const submit = async () => {
    if (await onAdd(email)) { setEmail(""); setEditing(false); }
  };

  if (!editing) {
    return (
      <div className="mb-3">
        <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors" data-testid="add-attendee-btn">
          <UserPlus className="h-3 w-3" /> Add attendee for AI brief
        </button>
      </div>
    );
  }
  return (
    <div className="mb-3 space-y-2">
      <div className="flex gap-2">
        <Input type="email" placeholder="Enter attendee email (e.g., john@company.com)" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} className="flex-1 bg-white/5 border-white/20 text-white text-xs h-8" data-testid="attendee-email-input" />
        <Button size="sm" onClick={submit} className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 h-8 text-xs" data-testid="attendee-submit-btn">Add</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEmail(""); }} className="text-gray-400 hover:text-white h-8 text-xs">Cancel</Button>
      </div>
      <p className="text-xs text-gray-500">Add attendee to generate AI intelligence brief</p>
    </div>
  );
};

export const MeetingCard = ({ meeting, index, onJoin, onAddAttendee }) => {
  const soon = isStartingSoon(meeting.start_time);
  const meetingLink = meeting.meeting_links?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(index, 10) * 0.1 }} whileHover={{ scale: 1.01, x: 4 }}
      className={`glass-card-light rounded-xl p-4 transition-all ${soon ? "border-ism-teal/30 shadow-[0_0_20px_rgba(0,163,138,0.1)]" : ""}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`meeting-card-${meeting.id}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MeetingTypeIcon source={meeting.source} />
          <span className="text-white text-sm truncate">{meeting.title}</span>
        </div>
        <Badge className={`text-xs flex-shrink-0 ${soon ? "bg-ism-teal/20 text-ism-teal border-ism-teal/30" : "bg-white/10 text-gray-400 border-white/10"}`}>
          {soon && <Zap className="h-3 w-3 mr-1" />}
          {getTimeUntilMeeting(meeting.start_time)}
        </Badge>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(meeting.start_time)} at {formatTime(meeting.start_time)}</div>
        {meeting.duration_minutes && <span className="text-gray-600">{meeting.duration_minutes} min</span>}
      </div>

      {meeting.attendees?.length > 0 ? <AttendeeChips attendees={meeting.attendees} /> : <AddAttendee onAdd={(email) => onAddAttendee(meeting.id, email)} />}

      <div className="flex items-center gap-2">
        {meetingLink && (
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button onClick={() => onJoin(meetingLink)} size="sm" data-testid={`join-meeting-${meeting.id}`}
              className={`${soon ? "bg-gradient-to-r from-ism-teal to-ism-teal/80 text-black font-semibold shadow-[0_0_20px_rgba(0,163,138,0.3)]" : "bg-gradient-to-r from-ism-teal to-ism-teal text-white"} text-xs h-7 rounded-lg border-0`}>
              <Play className="h-3 w-3 mr-1" /> Join {meetingLink.type === "zoom" ? "Zoom" : "Meeting"}
            </Button>
          </motion.div>
        )}
        {meeting.is_sales_meeting && <Badge className="bg-ism-teal/20 text-ism-teal border-ism-teal/30 text-xs">Sales Meeting</Badge>}
      </div>
    </motion.div>
  );
};

export const RecordingCard = ({ recording, index, onView }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(index, 10) * 0.1 }} whileHover={{ scale: 1.01, x: 4 }}
    className="glass-card-light rounded-xl p-4"
    onClick={(e) => e.stopPropagation()}
    data-testid={`recording-card-${recording.uuid || index}`}
  >
    <div className="flex items-start justify-between mb-2 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <FileVideo className="h-4 w-4 text-ism-teal flex-shrink-0" />
        <span className="text-white text-sm truncate">{recording.topic}</span>
      </div>
      <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(recording.start_time)}</span>
    </div>
    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
      {recording.duration && <span>{Math.round(recording.duration / 60)} min</span>}
      {recording.total_size && <span>{(recording.total_size / 1024 / 1024).toFixed(1)} MB</span>}
    </div>
    <div className="flex items-center gap-2">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button onClick={() => onView(recording)} size="sm" className="bg-gradient-to-r from-ism-teal to-ism-teal/80 hover:from-ism-teal/90 hover:to-ism-teal/70 text-white text-xs h-7 rounded-lg border-0">
          <Play className="h-3 w-3 mr-1" /> Watch Recording
        </Button>
      </motion.div>
      {recording.recording_files?.some((f) => f.recording_type === "audio_transcript") && (
        <Badge className="bg-gray-600/20 text-gray-400 border-gray-600/30 text-xs">Has Transcript</Badge>
      )}
    </div>
  </motion.div>
);
